import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for auth API path
 */
export const AUTH_API_PATH = new InjectionToken<string>('AUTH_API_PATH');

/**
 * Injection token for user API base path
 */
export const USER_API_PATH = new InjectionToken<string>('USER_API_PATH');

/**
 * Injection token for role upgrade API base path
 */
export const ROLE_UPGRADE_API_PATH = new InjectionToken<string>('ROLE_UPGRADE_API_PATH');

/**
 * Interface for user data response
 */
export interface UserData {
  [key: string]: any;
}

/**
 * Auth API Service for handling authentication-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  constructor(
    private networkService: NetworkService,
    private http: HttpClient,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(AUTH_API_PATH) private authApiPath: string,
    @Optional() @Inject(USER_API_PATH) private userApiPath?: string,
    @Optional() @Inject(ROLE_UPGRADE_API_PATH) private roleUpgradeApiPath?: string,
  ) {}

  /**
   * Get user information from the control plane API
   * This should be called after successful Keycloak login
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @returns Promise with user data
   */
  async getUserInfo(): Promise<UserData> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}user/`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Make the API call using NetworkService
      const userData = await this.networkService.get_api(
        url,
        headers,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return userData;
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  }

  /**
   * Get client credentials from the control plane API
   * @returns Promise with client credentials data
   */
  async getClientCredentials(): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}client`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Prepare payload - empty object as required
      const payload = {};

      // Make the API call using NetworkService (POST request)
      const credentials = await this.networkService.post_api(
        url,
        payload,
        headers,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return credentials;
    } catch (error) {
      console.error('Error fetching client credentials:', error);
      throw error;
    }
  }

  /**
   * Change user password
   * @param newPassword The new password to set
   * @returns Promise with the response data
   */
  async changePassword(newPassword: string): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}user/password`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Prepare request body
      const body = {
        new_password: newPassword,
      };

      // Make the API call using NetworkService (PUT request)
      const response = await this.networkService.put_api(
        url,
        body,
        headers
      );

      return response;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  /**
   * Request role upgrade (e.g., provider role)
   * @param role The role to request (e.g., 'provider')
   * @returns Promise with the response data
   */
  async requestRoleUpgrade(role: string = 'provider'): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}organization/user/provider_role/requests`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Prepare request body
      const body = {
        role: role,
      };

      // Make the API call using NetworkService
      const response = await this.networkService.post_api(
        url,
        body,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error('Error requesting role upgrade:', error);
      throw error;
    }
  }

  /**
   * Request compute role upgrade
   * @param body Optional request body. If not provided, defaults to empty object {}
   * @returns Promise with the response data
   */
  async computeRequest(body?: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}compute/requests`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Prepare request body - use provided body or default to empty object
      const requestBody = body !== undefined && body !== null ? body : {};

      // Make the API call using NetworkService
      const response = await this.networkService.post_api(
        url,
        requestBody,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error('Error requesting compute role:', error);
      throw error;
    }
  }

  /**
   * Get credit balance
   * @returns Promise with credit balance data
   */
  async getCreditBalance(): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}user/credit/balance`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        false, // disableToaster - set to false to show error toasters
      );

      return response;
    } catch (error) {
      console.error('Error getting credit balance:', error);
      throw error;
    }
  }

  /**
   * Withdraw the current user's credit request by ID.
   * DELETE user/credit/request/{id}
   * @param id The ID of the credit request to withdraw
   * @returns Promise with the response data
   */
  async withdrawUserCreditRequest(id: string): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.authApiPath}user/credit/request/${encodeURIComponent(id)}`;

      const response = await this.networkService.delete_api(
        url,
        {}, // body - empty object
        undefined,
      );

      return response;
    } catch (error) {
      console.error('Error withdrawing user credit request:', error);
      throw error;
    }
  }

  /**
   * Get the current user's credit requests (user/credit/request).
   * Use this to check for pending consent/request before allowing a new request.
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort parameter (default: 'requestedAt:desc')
   * @returns Promise with the user's credit request data (e.g. { result: [...], paginationInfo: {...} })
   */
  async getUserCreditRequest(page: number = 1, size: number = 10, sort: string = 'requestedAt:desc'): Promise<any> {
    try {
      const params = new URLSearchParams({ page: page.toString(), size: size.toString(), sort });
      const url = `${this.basePathUrl}${this.authApiPath}user/credit/request?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      return response;
    } catch (error) {
      console.error('Error fetching user credit request:', error);
      throw error;
    }
  }

  /**
   * Get all credit requests (GET credit/request).
   * Call this when opening the Request Credits History modal to load the list.
   * @param page Page number (default: 1)
   * @param size Page size (default: 100)
   * @param sort Optional sort, e.g. `requestedAt:desc` (omit for API default order)
   * @returns Promise with credit request data (e.g. { result: [...], paginationInfo: {...} })
   */
  async getAllCreditRequests(page: number = 1, size: number = 100, sort?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
      if (sort?.trim()) {
        params.append('sort', sort.trim());
      }
      const url = `${this.basePathUrl}${this.authApiPath}credit/request?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      return response;
    } catch (error) {
      console.error('Error fetching all credit requests:', error);
      throw error;
    }
  }

  /**
   * Get credit request list
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Optional sort, e.g. `requestedAt:desc`, `userName:asc` (omit initial load — API default order)
   * @returns Promise with credit request data
   */
  async getCreditRequest(page: number = 1, size: number = 10, sort?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
      if (sort?.trim()) {
        params.append('sort', sort.trim());
      }
      const url = `${this.basePathUrl}${this.authApiPath}credit/request?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        false, // disableToaster - set to false to show error toasters
      );

      return response;
    } catch (error) {
      console.error('Error getting credit request:', error);
      throw error;
    }
  }

  /**
   * Get credit request list (POST variant)
   * Uses the same `credit/request` endpoint but sends a POST body instead of query params.
   * This is useful when the API supports complex filters in the request payload.
   *
   * @param payload Request body to send to the API (e.g., filters, search criteria)
   * @param page Page number (optional) – if provided and the API expects it in the body
   * @param size Page size (optional) – if provided and the API expects it in the body
   */
  async getCreditRequestPost(payload: any = {}, page?: number, size?: number): Promise<any> {
    try {
      // Attach page/size into payload if caller provided them and backend expects in body
      const requestBody: any = {
        ...payload,
      };
      if (page !== undefined) {
        requestBody.page = page;
      }
      if (size !== undefined) {
        requestBody.size = size;
      }

      const url = `${this.basePathUrl}${this.authApiPath}credit/request`;

      const response = await this.networkService.post_api(
        url,
        requestBody,
        undefined, // headers - interceptors handle Content-Type and Authorization
        false, // disableToaster - keep false to show error toasters
      );

      return response;
    } catch (error) {
      console.error('Error getting credit request (POST):', error);
      throw error;
    }
  }

  /**
   * Approve or reject credit request
   * @param requestId The ID of the credit request to update
   * @param status The status to set ('granted' for approve, 'rejected' for reject)
   * @param payload Optional payload with additional fields (newCredits, expirationDate, etc.)
   * @returns Promise with response
   */
  async approveCreditRequest(requestId: string, status: 'granted' | 'rejected', payload?: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}credit/request`;

      // Prepare request body with status and any additional payload
      const body = {
        status: status,
        ...payload, // Spread any additional fields (newCredits, expirationDate, etc.)
      };

      // Make PUT API call using NetworkService
      // Note: put_api only accepts (url, body, header) - headers are handled by interceptors
      // Headers (Content-Type, Authorization) are automatically added by interceptors
      const response = await this.networkService.put_api(
        url,
        body,
        undefined // headers - handled by interceptors
      );

      return response;
    } catch (error) {
      console.error(`Error updating credit request ${requestId} with status ${status}:`, error);
      throw error;
    }
  }

  /**
   * Get compute request list
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Optional sort, e.g. `userName:asc`, `created_at:desc`
   * @returns Promise with compute request data
   */
  async getComputeRequest(page: number = 1, size: number = 10, sort?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
      if (sort?.trim()) {
        params.append('sort', sort.trim());
      }
      const url = `${this.basePathUrl}${this.authApiPath}compute/requests?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        false, // disableToaster - set to false to show error toasters
      );

      return response;
    } catch (error) {
      console.error('Error getting compute request:', error);
      throw error;
    }
  }

  /**
   * Approve or reject compute request
   * @param requestId The ID of the compute request to update
   * @param status The status to set ('granted' for approve, 'rejected' for reject)
   * @returns Promise with response
   */
  async approveComputeRequest(requestId: string, status: 'granted' | 'rejected'): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}compute/requests/${requestId}`;

      // Prepare request body with status from component
      const body = {
        status: status,
      };

      // Make PUT API call using NetworkService
      // Note: put_api only accepts (url, body, header) - headers are handled by interceptors
      // Headers (Content-Type, Authorization) are automatically added by interceptors
      const response = await this.networkService.put_api(
        url,
        body,
        undefined // headers - handled by interceptors
      );

      return response;
    } catch (error) {
      console.error(`Error updating compute request ${requestId} with status ${status}:`, error);
      throw error;
    }
  }

  /**
   * Get all platform users from the control plane API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param searchTerm Optional search term to filter users (minimum 3 characters)
   * @returns Promise with users data
   */
  async getAllPlatformUsers(page: number = 1, size: number = 10, searchTerm?: string): Promise<any> {
    try {
      // Construct the full URL with query parameters
      // Using injected basePathUrl and authApiPath
      let url = `${this.basePathUrl}${this.authApiPath}admin/user?page=${page}&size=${size}`;
      
      // Add search_term parameter if provided and has at least 3 characters
      if (searchTerm && searchTerm.trim().length >= 3) {
        url += `&search_term=${encodeURIComponent(searchTerm.trim())}`;
      }

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const usersData = await this.networkService.get_api(
        url,
        undefined,
        false, // Toaster - set to true if you don't want error toasters
      );

      return usersData;
    } catch (error) {
      console.error('Error fetching all platform users:', error);
      throw error;
    }
  }

  /**
   * Get user information by user ID from the control plane API
   * This method calls the admin endpoint to get user details by ID
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @param userId The user ID to fetch information for
   * @returns Promise with user data
   */
  async getUserInfoById(userId: string): Promise<UserData> {
    try {
      // Construct the full URL
      // Using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}admin/user/${userId}`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Make the API call using NetworkService
      const userData = await this.networkService.get_api(
        url,
        headers,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return userData;
    } catch (error) {
      console.error('Error fetching user info by ID:', error);
      throw error;
    }
  }

  /**
   * Deactivate a user by user ID
   * This method calls the admin endpoint to update/deactivate a user
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @param userId The user ID to deactivate
   * @param updateData Optional data to update (defaults to { status: "deactivate" })
   * @returns Promise with the response data
   */
  async deactivateUser(userId: string, updateData?: any): Promise<any> {
    try {
      // Construct the full URL
      // Using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}admin/${userId}/update`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Prepare request body (default to deactivating the account if no data provided)
      const body = updateData || { status: 'deactivate' };

      // Make the API call using NetworkService
      const response = await this.networkService.post_api(
        url,
        body,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error(`Error deactivating user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get asset requests from the control plane API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param type Optional request type filter (e.g. 'API', 'SFTP')
   * @param sort Optional sort string (e.g. 'requestedAt:desc'), same convention as other control-plane list endpoints
   * @returns Promise with asset requests data
   */
  async getAssetRequests(page: number = 1, size: number = 10, type?: string, sort?: string): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      if (type && String(type).trim() !== '') {
        params.append('type', String(type).trim());
      }
      if (sort != null && String(sort).trim() !== '') {
        params.append('sort', String(sort).trim());
      }

      const url = `${this.basePathUrl}${this.authApiPath}asset/request?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching asset requests:', error);
      throw error;
    }
  }

  /**
   * Create a data upload request (e.g., SFTP upload configuration)
   * POST: {basePathUrl}{authApiPath}asset/request
   * @param payload The payload object containing upload request data (sent from component)
   * @returns Promise with response
   */
  async createDataUploadRequest(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.authApiPath}asset/request`;

      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error('Error creating data upload request:', error);
      throw error;
    }
  }

  /**
   * Approve or reject an asset request
   * @param requestId The ID of the asset request to update
   * @param payload The payload containing the request data (will be sent from component)
   * @returns Promise with response
   */
  async approveAssetRequest(requestId: string, payload: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}asset/request/${requestId}`;

      // Make PUT API call using NetworkService
      // Note: put_api only accepts (url, body, header) - headers are handled by interceptors
      // Headers (Content-Type, Authorization) are automatically added by interceptors
      const response = await this.networkService.put_api(
        url,
        payload,
        undefined // headers - handled by interceptors
      );

      return response;
    } catch (error) {
      console.error(`Error updating asset request ${requestId}:`, error);
      throw error;
    }
  }

  /**
   * Get organisation requests from the control plane API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort parameter (default: 'createdAt:desc')
   * @param entityType Optional entity type filter (e.g., 'PARTNERSHIP', 'PRIVATE_LIMITED')
   * @param status Optional status filter - can be a single string or array of strings (e.g., 'pending', ['granted', 'rejected'])
   * @returns Promise with organisation requests data
   */
  async getOrganisationRequests(page: number = 1, size: number = 10, sort: string = 'createdAt:desc', entityType?: string, status?: string | string[]): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', sort);

      // Add entityType filter if provided
      if (entityType) {
        params.append('entityType', entityType);
      }

      // Add status filter(s) if provided
      if (status) {
        if (Array.isArray(status)) {
          // Multiple status values - append each one
          status.forEach(s => params.append('status', s));
        } else {
          // Single status value
          params.append('status', status);
        }
      }

      const url = `${this.basePathUrl}${this.authApiPath}organisations/requests?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching organisation requests:', error);
      throw error;
    }
  }

  /**
   * Get all organisations from the control plane API
   * @param page Page number (default: 1)
   * @param size Page size (default: 100)
   * @returns Promise with organisations data
   */
  async getAllOrganisations(page: number = 1, size: number = 100): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      const url = `${this.basePathUrl}${this.authApiPath}organisations?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching all organisations:', error);
      throw error;
    }
  }

  /**
   * Get join organisation requests
   * @param organisationId The ID of the organisation
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param dateRange Optional date range filter { start: string, end: string }
   * @returns Promise with join requests data
   */
  async getJoinOrganisationRequests(
    organisationId: string,
    page: number = 1,
    size: number = 10,
    dateRange?: { start: string; end: string } | null
  ): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      // Add date range query params if provided
      if (dateRange && dateRange.start && dateRange.end) {
        // Format dates to ISO format with time
        let startDate: string;
        let endDate: string;
        
        // Handle different date formats - convert to string first
        const startValue: any = dateRange.start;
        const endValue: any = dateRange.end;
        
        // Format start date
        if (startValue instanceof Date) {
          // Convert Date object to ISO string and ensure it starts at 00:00:00
          const dateStr = startValue.toISOString().split('T')[0];
          startDate = `${dateStr}T00:00:00`;
        } else if (typeof startValue === 'string') {
          startDate = startValue.includes('T') 
            ? startValue 
            : `${startValue}T00:00:00`;
        } else {
          startDate = String(startValue);
        }
        
        // Format end date
        if (endValue instanceof Date) {
          // Convert Date object to ISO string and ensure it ends at 23:59:59
          const dateStr = endValue.toISOString().split('T')[0];
          endDate = `${dateStr}T23:59:59`;
        } else if (typeof endValue === 'string') {
          endDate = endValue.includes('T') 
            ? endValue 
            : `${endValue}T23:59:59`;
        } else {
          endDate = String(endValue);
        }
        
        params.append('time', startDate);
        params.append('endtime', endDate);
        params.append('timerel', 'between');
      }

      const url = `${this.basePathUrl}${this.authApiPath}organisations/${organisationId}/join_requests?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error(`Error fetching join organisation requests for ${organisationId}:`, error);
      throw error;
    }
  }

  /**
   * Join an organisation by submitting a join request
   * @param organisationId The ID of the organisation to join
   * @param payload The request payload (will be sent from component)
   * @returns Promise with the response data
   */
  async joinOrganisation(organisationId: string, payload: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}organisations/${organisationId}/join_requests`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - undefined as interceptors handle it
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error(`Error joining organisation ${organisationId}:`, error);
      throw error;
    }
  }

  /**
   * Logout / invalidate session on the control plane API.
   * Call this before Keycloak logout so the control plane can blacklist/revoke the token.
   * If the endpoint does not exist (404), the call will fail silently.
   * @returns Promise with the response, or void if the endpoint is not implemented
   */
  async logoutSession(): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.authApiPath}user/logout`;
      return await this.networkService.post_api(
        url,
        {},
        { 'Content-Type': 'application/json' },
        false, // disableToaster - don't show error for optional logout
        false, // disableLoader - don't show loader during logout
      );
    } catch (error) {
      // Endpoint may not exist (404) - log and continue
      console.warn('Control plane logout endpoint not available or failed:', error);
      return;
    }
  }

  /**
   * Delete a user from the control plane API
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @returns Promise with the response data
   */
  async deleteUser(): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}user/delete`;

      // Prepare payload - empty object as required
      const payload = {};

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.delete_api(
        url,
        payload, // body - empty object as required
        undefined // headers - undefined as interceptors handle it
      );

      return response;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Upload an asset file to the file service
   * This method uploads a file to the /v1/assets endpoint
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @param file The file to upload
   * @param fileBaseUrl Optional base URL for file service (defaults to staging.files.tgdex.in)
   * @returns Promise with upload response including status code
   */
  /**
   * Create an organisation request
   * This method creates a new organisation request by posting to organisations/requests endpoint
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @param payload The request payload containing organisation details
   * @returns Promise with the response data
   */
  async createOrganisationRequest(payload: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}organisations/requests`;

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Make the API call using NetworkService (POST request)
      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error('Error creating organisation request:', error);
      throw error;
    }
  }

  /**
   * Get provider role requests from the control plane API.
   * Accepts either `start/end` or `startDate/endDate` for date filtering.
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param dateRange Optional date range filter
   * @returns Promise with provider role requests data
   */
  async providerRoleRequest(
    page: number = 1,
    size: number = 10,
    dateRange?:
      | {
          start?: string | Date | null;
          end?: string | Date | null;
          startDate?: string | Date | null;
          endDate?: string | Date | null;
        }
      | null
  ): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      const startValue = dateRange?.startDate ?? dateRange?.start;
      const endValue = dateRange?.endDate ?? dateRange?.end;

      if (startValue && endValue) {
        const startDate =
          startValue instanceof Date
            ? `${startValue.toISOString().split('T')[0]}T00:00:00`
            : String(startValue).includes('T')
              ? String(startValue)
              : `${String(startValue)}T00:00:00`;

        const endDate =
          endValue instanceof Date
            ? `${endValue.toISOString().split('T')[0]}T23:59:59`
            : String(endValue).includes('T')
              ? String(endValue)
              : `${String(endValue)}T23:59:59`;

        params.append('time', startDate);
        params.append('endtime', endDate);
        params.append('timerel', 'between');
      }

      const url = `${this.basePathUrl}${this.authApiPath}organization/user/provider_role/requests?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return response;
    } catch (error) {
      console.error('Error fetching provider role requests:', error);
      throw error;
    }
  }

  /**
   * Approve or reject a provider role request
   * @param requestId The ID of the provider role request
   * @param payload The payload containing the status ('granted' for approve, 'rejected' for reject)
   * @returns Promise with response
   */
  async approveRejectProviderRole(requestId: string, payload: { status: string }): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}organization/user/provider_role/requests/${requestId}`;

      // Make PUT API call using NetworkService
      // Note: put_api only accepts (url, body, header) - headers are handled by interceptors
      // Headers (Content-Type, Authorization) are automatically added by interceptors
      const response = await this.networkService.put_api(
        url,
        payload,
        undefined // headers - handled by interceptors
      );

      return response;
    } catch (error) {
      console.error(`Error approving/rejecting provider role request ${requestId}:`, error);
      throw error;
    }
  }

  /**
   * Withdraw an organisation join/create request (user-initiated cancel of pending request).
   * PATCH {baseURL}{authPathURL}/organisation/join-request/{requestId}
   * @param id The ID of the join request to withdraw
   * @returns Promise with response
   */
  async withdrawOrganisationRequest(id: string): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.authApiPath}organisation/join-request/${id}`;
      const body = { status: 'withdrawn' };
      const response = await this.networkService.patch_api(
        url,
        body,
        undefined
      );
      return response;
    } catch (error) {
      console.error(`Error withdrawing organisation request ${id}:`, error);
      throw error;
    }
  }

  /**
   * Withdraw a join organisation request (user-initiated cancel of pending request)
   * @deprecated Use withdrawOrganisationRequest(id) instead - calls PATCH organisation/join-request/{id}
   * @param organisationId The ID of the organisation
   * @param requestId The ID of the join request to withdraw
   * @returns Promise with response
   */
  async withdrawJoinOrganisationRequest(organisationId: string, requestId: string): Promise<any> {
    return this.updateJoinOrganisationRequestStatus(organisationId, requestId, { status: 'withdrawn' });
  }

  /**
   * Approve or reject a join organisation request
   * @param organisationId The ID of the organisation
   * @param requestId The ID of the join request to update
   * @param payload The payload containing the status ('granted' for approve, 'rejected' for reject)
   * @returns Promise with response
   */
  async updateJoinOrganisationRequestStatus(organisationId: string, requestId: string, payload: { status: string }): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}organisations/${organisationId}/join_requests/${requestId}`;

      // Make PUT API call using NetworkService
      // Note: put_api only accepts (url, body, header) - headers are handled by interceptors
      // Headers (Content-Type, Authorization) are automatically added by interceptors
      const response = await this.networkService.put_api(
        url,
        payload,
        undefined // headers - handled by interceptors
      );

      return response;
    } catch (error) {
      console.error(`Error updating join organisation request ${requestId} status:`, error);
      throw error;
    }
  }

  /**
   * Approve an organisation request
   * @param payload Approval payload (will be sent from component)
   * @returns Promise with approval response
   */
  async approveOrganisationRequest(payload: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and authApiPath
      const url = `${this.basePathUrl}${this.authApiPath}organisations/requests/approve`;

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
      };

      console.log('=== approveOrganisationRequest API Call ===');
      console.log('URL:', url);
      console.log('Payload:', payload);
      console.log('Payload (JSON):', JSON.stringify(payload, null, 2));

      // Make POST API call using NetworkService
      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      console.log('Response:', response);
      console.log('Response (JSON):', JSON.stringify(response, null, 2));
      console.log('=== End approveOrganisationRequest API Call ===');

      return response;
    } catch (error) {
      console.error('Error approving organisation request:', error);
      throw error;
    }
  }

  /**
   * Upload an asset file to the file service
   * This method uploads a file to the /v1/assets endpoint
   * Note: The auth token is automatically added by the authTokenInterceptor
   * @param file The file to upload
   * @param fileBaseUrl Optional base URL for file service (defaults to staging.files.tgdex.in)
   * @returns Promise with upload response including status code
   */
  async uploadAsset(file: File, fileBaseUrl?: string): Promise<{ data: any; status: number }> {
    try {
      // Use provided fileBaseUrl or default to staging file service
      const baseUrl = fileBaseUrl || 'https://staging.files.tgdex.in';
      const url = `${baseUrl}/v1/assets`;

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Use HttpClient directly with observe: 'response' to get status code
      // The browser will automatically set Content-Type with boundary for multipart/form-data
      // The auth token is automatically added by the authTokenInterceptor
      return new Promise((resolve, reject) => {
        this.http.post(url, formData, {
          headers: new HttpHeaders({
            'accept': 'application/json, text/plain, */*',
          }), // Browser sets Content-Type automatically for FormData
          observe: 'response' as const,
        }).subscribe({
          next: (response) => {
            resolve({
              data: response.body,
              status: response.status,
            });
          },
          error: (error) => {
            console.error('Error uploading asset:', error);
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error('Error uploading asset:', error);
      throw error;
    }
  }

  
}
